/**
 * MyLifePrompt — "My Life" with integrated AI search (pattern 1b).
 *
 * Four bands:
 *  1. Greeting (name + vitals)
 *  2. Prompt bar (search + voice + chips)
 *  3. Brief (since-last-visit + pending voices)
 *  4. Content (chapters timeline OR search results)
 *
 * Left rail: person switcher + recent questions.
 * Band 4 alone re-renders on question; bands 1-3 stay fixed.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BookOpen, Search, Mic, MicOff, Plus, Feather, ArrowLeft, ArrowRight,
  Sparkles, Clock, Camera, MessageCircle, ChevronRight, Play, Loader2,
  Trophy, Users,
} from 'lucide-react';
import HubLayout from '../components/common/HubLayout';
import { useChaptersData } from '../hooks/useChaptersData';
import { getUser } from '../utils/secureStorage';
import { axiosInstance } from '../services/api';
import { askQuestion, getSuggestionChips, getSearchHistory } from '../services/retrievalAPI';
import { getPendingConfirmations } from '../services/videoActivationAPI';
import { getMediaServeUrl } from '../config/mediaAPI';
import { StoryCard } from '../components/dashboard/FeedTimeline';
import { lifeEventAPI } from '../config/lifeEventAPI';
import ChaptersOnboarding from '../components/chapters/ChaptersOnboarding';

const FONT_H = "'Lora', Georgia, serif";
const FONT_B = "'Source Sans 3', sans-serif";
const CH_COLORS = ['#6B8E23', '#8B7355', '#556B2F', '#7B6B8E', '#8E6B23'];
const chColor = (i) => CH_COLORS[(i - 1) % CH_COLORS.length];
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, ''); // strip /api suffix for absolute URLs

function resolveThumbUrl(m) {
  if (!m) return '';
  if (m.file_url && m.file_url.startsWith('http')) return m.file_url;
  if (m.url && m.url.startsWith('http')) return m.url;
  if (m.url && m.url.startsWith('/')) return `${API_ORIGIN}${m.url}`;
  if (m.id) return getMediaServeUrl(m.id);
  return '';
}

// --- Voice helpers (reused from AskPage) ---
const getSpeechRecognition = () => {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;
  const r = new SR();
  if ('processLocally' in r) r.processLocally = true;
  r.continuous = false;
  r.interimResults = true;
  r.lang = navigator.language || 'en';
  return r;
};

// ─── BAND 1: Greeting ───
function Greeting({ profile, totalChapters, totalStories }) {
  const birthYear = profile?.birth_year ? `Born ${profile.birth_year}` : '';
  const hasStories = totalStories > 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        background: '#6E531A',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <BookOpen size={22} color="#fff" />
      </div>
      <div>
        <h1 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 24, color: '#2C2F26', margin: 0, lineHeight: 1.1 }}>
          The Life of {profile?.first_name || 'You'}
        </h1>
        <p style={{ fontFamily: FONT_B, fontSize: 14.5, color: '#4A4F40', marginTop: 3, margin: '3px 0 0' }}>
          {birthYear}
          {hasStories && birthYear && ' · '}
          {hasStories && `${totalChapters} chapters · ${totalStories} ${totalStories === 1 ? 'story' : 'stories'}`}
        </p>
      </div>
    </div>
  );
}

// ─── BAND 2: Prompt Bar ───
function PromptBar({ onSubmit, suggestions, activeQuery }) {
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const [voiceText, setVoiceText] = useState('');
  const [expanded, setExpanded] = useState(!!activeQuery);
  const recRef = useRef(null);
  const hasVoice = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const q = (text || voiceText).trim();
    if (q) { onSubmit(q); setText(''); setVoiceText(''); }
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = navigator.language || 'en';
    recRef.current = r;
    setListening(true);
    setVoiceText('');
    r.onresult = (e) => {
      const last = e.results[e.results.length - 1];
      const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
      setVoiceText(transcript);
      if (last.isFinal) {
        setText(transcript);
        setListening(false);
      }
    };
    r.onerror = (e) => {
      console.error('Speech recognition error:', e.error);
      setListening(false);
    };
    r.onend = () => setListening(false);
    try {
      r.start();
    } catch (err) {
      console.error('Speech recognition start failed:', err);
      setListening(false);
    }
  };

  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 24 }}>
      {/* Collapsed state */}
      {!expanded && !activeQuery && (
        <button onClick={() => setExpanded(true)} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          background: '#fff', borderRadius: 14, padding: '14px 18px',
          border: '2px solid #9A9F8A', cursor: 'pointer', minHeight: 52,
          fontFamily: FONT_B, fontSize: 16, color: '#4A4F40', textAlign: 'left',
        }}>
          <Search size={18} color="#5A6150" />
          Ask about your life
        </button>
      )}

      {/* Expanded state */}
      {(expanded || activeQuery) && (
      <form onSubmit={handleSubmit} style={{
        display: 'flex', gap: 8, alignItems: 'center',
        background: '#fff', borderRadius: 18, padding: '9px 9px 9px 20px',
        border: `2px solid ${focused ? '#6B8E23' : '#D6D3C2'}`,
        boxShadow: '0 6px 20px rgba(40,46,28,.07)', transition: 'border-color 0.15s',
      }}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      >
        <Search size={21} color="#9AA088" style={{ flexShrink: 0 }} />
        <input
          value={listening ? voiceText : (text || activeQuery || '')}
          onChange={e => setText(e.target.value)}
          placeholder="Ask about a chapter, a year, or something you once said\u2026"
          disabled={listening}
          onFocus={() => { if (!text && activeQuery) setText(activeQuery); }}
          style={{
            flex: 1, border: 'none', outline: 'none', fontSize: 18.5,
            fontFamily: FONT_B, color: '#2C2F26', background: 'transparent',
            minHeight: 40,
          }}
        />
        {hasVoice && (
          <button type="button" onClick={listening ? () => { recRef.current?.stop(); setListening(false); } : startVoice}
            style={{
              background: listening ? '#C74400' : '#E65100',
              border: 'none', borderRadius: 14, height: 54, padding: '0 22px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: 15, gap: 6, fontFamily: FONT_B,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (!listening) e.currentTarget.style.background = '#C74400'; }}
            onMouseLeave={e => { if (!listening) e.currentTarget.style.background = '#E65100'; }}
          >
            {listening ? <MicOff size={18} /> : <><Mic size={17} /> Speak</>}
          </button>
        )}
        <button type="submit" disabled={!(text || voiceText).trim()}
          style={{
            background: (text || voiceText).trim() ? '#556B2F' : '#D4D1C4',
            color: '#fff', border: 'none', borderRadius: 14, padding: '0 20px',
            fontSize: 16, fontWeight: 700, cursor: (text || voiceText).trim() ? 'pointer' : 'not-allowed',
            height: 54, fontFamily: FONT_B,
          }}>Ask</button>
      </form>
      )}
      {/* Chips */}
      {(expanded || activeQuery) && suggestions.length > 0 && (
        <div style={{ display: 'flex', gap: 9, marginTop: 10, flexWrap: 'wrap' }}>
          {suggestions.slice(0, 3).map((chip, i) => (
            <button key={i} onClick={() => { setText(chip); onSubmit(chip); }}
              style={{
                background: '#fff', border: '1.5px solid #DCD9C8', borderRadius: 999,
                padding: '9px 15px', fontSize: 15, color: '#556B2F', cursor: 'pointer',
                fontFamily: FONT_B, minHeight: 44, transition: 'border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#B9C3A0'; e.currentTarget.style.color = '#4A5D23'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#DCD9C8'; e.currentTarget.style.color = '#556B2F'; }}
            >{chip}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Reminders (§4) ───
function Reminders({ pendingVoices, pendingJobId, navigate }) {
  if (pendingVoices <= 0) return null;

  const voiceRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px' }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%', background: '#FFF3E6',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Users size={19} color="#A63C00" />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 16.5, fontWeight: 700, color: '#2C2F26', margin: 0, fontFamily: FONT_B }}>
          {pendingVoices} voice{pendingVoices > 1 ? 's need' : ' needs'} a name
        </p>
      </div>
      {pendingJobId && (
        <button onClick={() => navigate(`/identify-voices/${pendingJobId}`)} style={{
          background: '#3E4F22', color: '#fff', border: 'none', borderRadius: 12,
          padding: '0 16px', fontWeight: 700, fontSize: 15.5, cursor: 'pointer',
          fontFamily: FONT_B, minHeight: 48, whiteSpace: 'nowrap',
        }}>
          Name the voice{pendingVoices > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        background: '#fff', border: '1.5px solid #D6D1BD', borderRadius: 14,
        overflow: 'hidden',
      }}>
        {voiceRow}
      </div>
    </div>
  );
}

// ─── BAND 4: Ask Results ───
function AskResults({ response, loading, onBack, query }) {
  const questionEcho = query ? (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%', background: '#3E4F22', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 13, fontWeight: 700,
      }}>
        <Search size={15} />
      </div>
      <p style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 20, color: '#2C2F26', margin: 0, lineHeight: 1.3 }}>
        {query}
      </p>
    </div>
  ) : null;

  if (loading) {
    return (
      <div>
        <button onClick={onBack} style={{
          display: 'flex', alignItems: 'center', gap: 4, background: 'none',
          border: 'none', color: '#556B2F', fontSize: 14, fontWeight: 600,
          cursor: 'pointer', padding: '8px 0', marginBottom: 12, fontFamily: FONT_B,
        }}>
          <ArrowLeft size={16} /> Back to all chapters
        </button>
        {questionEcho}
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Loader2 size={28} color="#556B2F" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#828A76', marginTop: 10, fontSize: 15 }}>Searching your life story...</p>
        </div>
      </div>
    );
  }

  if (!response) return null;

  const relResult = response.relationship_result;
  const isEmpty = !relResult && (response.status === 'empty' || !response.items?.length);
  const isMarginal = response.meta?.confidence === 'marginal';

  return (
    <div>
      {/* Back link */}
      <button onClick={onBack} style={{
        display: 'flex', alignItems: 'center', gap: 4, background: 'none',
        border: 'none', color: '#556B2F', fontSize: 14, fontWeight: 600,
        cursor: 'pointer', padding: '8px 0', marginBottom: 12, fontFamily: FONT_B,
      }}>
        <ArrowLeft size={16} /> Back to all chapters
      </button>

      {/* Question echo */}
      {questionEcho}

      {/* Relationship result */}
      {relResult ? (
        <div style={{
          background: '#fff', borderRadius: 16, border: '1px solid #E4E1D2',
          padding: '24px 20px',
        }}>
          <p style={{
            fontFamily: FONT_H, fontWeight: 600, fontSize: 18,
            color: '#2C2F26', margin: '0 0 16px', lineHeight: 1.4,
          }}>
            {relResult.description}
          </p>
          {relResult.people?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {relResult.people.map(p => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: '#F7F6EE', border: '1px solid #E4E1D2', borderRadius: 14,
                  padding: '12px 16px',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', background: '#556B2F',
                    color: '#fff', fontSize: 15, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{p.initial}</div>
                  <span style={{ fontFamily: FONT_B, fontSize: 16, fontWeight: 600, color: '#2C2F26' }}>
                    {p.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : isEmpty ? (
        <div style={{
          textAlign: 'center', padding: 32, background: '#fff',
          borderRadius: 16, border: '1px solid #E4E1D2',
        }}>
          <Feather size={32} color="#B9C3A0" style={{ marginBottom: 10 }} />
          <p style={{ fontFamily: FONT_H, fontSize: 19, fontWeight: 700, color: '#2C2F26', margin: '0 0 6px' }}>
            Nothing found for that yet
          </p>
          <p style={{ color: '#6E7562', fontSize: 15, margin: 0, fontFamily: FONT_B }}>
            Try asking about a specific person, chapter, or time in your life.
          </p>
          {response.framing_text && (
            <p style={{ color: '#6E7562', fontSize: 14, marginTop: 8, fontStyle: 'italic', fontFamily: FONT_B }}>
              {response.framing_text}
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Framing text */}
          <div style={{
            padding: '14px 18px', borderRadius: 14, marginBottom: 14,
            background: isMarginal ? '#FFF8E7' : '#F0F5E8',
            border: `1px solid ${isMarginal ? '#E8D5A0' : '#C8D5B0'}`,
          }}>
            <p style={{
              fontFamily: FONT_H, fontWeight: 500, fontSize: 16,
              color: isMarginal ? '#6B5A1A' : '#3A4A20', margin: 0, lineHeight: 1.5,
            }}>
              {response.framing_text || "Here are the most relevant items from your life story."}
            </p>
            {isMarginal && (
              <p style={{ fontSize: 12, color: '#8B7A3A', marginTop: 4, fontStyle: 'italic', margin: '4px 0 0' }}>
                These may not be exactly what you're looking for.
              </p>
            )}
          </div>

          {/* Result cards */}
          {response.items?.map((item, i) => (
            <div key={item.item_id} style={{
              background: '#fff', borderRadius: 14, padding: 14,
              marginBottom: 10, border: '1px solid #E4E1D2',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: '#828A76', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                    {item.content_type === 'transcript_chunk' ? 'Recording' : item.content_type === 'story' ? 'Story' : item.content_type === 'wisdom_moment' ? 'Wisdom' : item.content_type}
                  </span>
                  {(item.author_name || item.speaker_name) && (
                    <span style={{ fontSize: 12, color: '#6E7562', fontFamily: FONT_B }}>
                      &middot; {item.content_type === 'story' ? `by ${item.author_name}` : item.speaker_name}
                    </span>
                  )}
                </div>
                {item.content_date && <span style={{ fontSize: 11, color: '#9AA088' }}>{item.content_date}</span>}
              </div>
              <p style={{ fontSize: 15, color: '#2C2F26', margin: '0 0 6px', lineHeight: 1.5, fontFamily: FONT_B }}>
                {item.text_preview}
              </p>
              {item.reason && (
                <p style={{ fontSize: 13, color: '#556B2F', fontStyle: 'italic', margin: 0 }}>{item.reason}</p>
              )}
              {item.start_ms != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 12, color: '#556B2F' }}>
                  <Play size={12} /> {Math.floor(item.start_ms / 60000)}:{String(Math.floor((item.start_ms % 60000) / 1000)).padStart(2, '0')}
                </div>
              )}
              {/* Story link footer */}
              {item.story_id && item.story_title && (
                <>
                  <div style={{ height: 1, background: '#EFEDE2', margin: '10px 0' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <BookOpen size={14} color="#556B2F" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: '#6E7562', fontFamily: FONT_B, flex: 1 }}>
                      From <strong style={{ color: '#2C2F26' }}>{item.story_title}</strong>
                    </span>
                    <a href={`/stories/${item.story_id}`}
                      onClick={e => { e.stopPropagation(); }}
                      style={{ fontSize: 13, fontWeight: 600, color: '#556B2F', fontFamily: FONT_B, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}
                    >Open story <ArrowRight size={12} /></a>
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Follow-ups */}
          {response.follow_up_questions?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 13, color: '#828A76', marginBottom: 6 }}>You might also ask:</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {response.follow_up_questions.map((q, i) => (
                  <button key={i} style={{
                    background: '#F0F5E8', border: '1px solid #C8D5B0', borderRadius: 20,
                    padding: '7px 14px', fontSize: 13, color: '#3A4A20', cursor: 'pointer',
                    fontFamily: FONT_B, minHeight: 36,
                  }}>
                    {q} <ChevronRight size={12} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── SIDEBAR: Search History ───
function SearchHistorySidebar({ entries, activeId, onSelect }) {
  if (!entries.length) return null;

  // Group by relative day
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now - 86400000).toDateString();
  const groups = {};
  entries.forEach(e => {
    const d = new Date(e.asked_at);
    let label;
    if (d.toDateString() === today) label = 'Today';
    else if (d.toDateString() === yesterday) label = 'Yesterday';
    else label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!groups[label]) groups[label] = [];
    groups[label].push(e);
  });

  return (
    <div className="nav-section" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
      <div className="nav-section-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clock size={12} /> You asked recently
      </div>
      {Object.entries(groups).map(([label, items]) => (
        <div key={label}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9AA088', textTransform: 'uppercase', letterSpacing: '1px', padding: '8px 14px 2px', fontFamily: FONT_B }}>
            {label}
          </div>
          {items.map(entry => {
            const isActive = entry.id === activeId;
            const time = new Date(entry.asked_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            return (
              <a key={entry.id}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => onSelect(entry)}
                style={{
                  borderLeft: isActive ? '3px solid #E65100' : '3px solid transparent',
                  cursor: 'pointer',
                  display: 'block',
                }}
              >
                <div style={{ fontSize: 13, lineHeight: 1.3, color: isActive ? '#2C2F26' : '#6E7562' }}>
                  {entry.query_text.length > 45 ? entry.query_text.slice(0, 45) + '...' : entry.query_text}
                </div>
                <div style={{ fontSize: 11, color: '#9AA088', marginTop: 2 }}>
                  {time} · {entry.result_count} result{entry.result_count !== 1 ? 's' : ''}
                </div>
              </a>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Person Switcher (§2-3: header control with searchable dropdown) ───
function PersonSwitcher({ currentName, children: kids, selectedChildId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // All people: self + children
  const allPeople = [
    { id: null, name: currentName, initial: (currentName || '?')[0].toUpperCase() },
    ...(kids || []).map(c => ({
      id: c.id, name: c.first_name || c.name || '?',
      initial: (c.first_name || '?')[0].toUpperCase(),
    })),
  ];

  const activeName = selectedChildId
    ? (allPeople.find(p => p.id === selectedChildId)?.name || '?')
    : currentName;
  const activeInitial = (activeName || '?')[0].toUpperCase();

  // Filter
  const filtered = search
    ? allPeople.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    : allPeople;

  // Group alphabetically (skip if <10)
  const grouped = {};
  filtered.forEach(p => {
    const letter = (p.name || '?')[0].toUpperCase();
    if (!grouped[letter]) grouped[letter] = [];
    grouped[letter].push(p);
  });

  const AVATAR_COLORS = ['#556B2F', '#8E6B23', '#3B72C4', '#6B3FA0', '#E65100', '#7B6B8E'];
  const avatarColor = (name) => AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];

  if (allPeople.length <= 1) return null; // no switcher if only self

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      {/* Pill button */}
      <button onClick={() => { setOpen(!open); setSearch(''); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          border: '1.5px solid #DCD9C8', borderRadius: 999, padding: '7px 14px 7px 10px',
          background: '#fff', cursor: 'pointer', minHeight: 44, fontFamily: FONT_B,
        }}>
        <span style={{ fontSize: 11, color: '#9AA088', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>
          Whose life
        </span>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', background: avatarColor(activeName),
          color: '#fff', fontSize: 12, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{activeInitial}</div>
        <span style={{ fontWeight: 700, fontSize: 15, color: '#2C2F26' }}>{activeName}</span>
        <ChevronRight size={14} color="#9AA088" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 52, right: 0, width: 260, zIndex: 100,
          background: '#fff', border: '1px solid #E4E1D2', borderRadius: 14,
          boxShadow: '0 14px 34px rgba(40,46,28,.14)', overflow: 'hidden',
        }}>
          {/* Search */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #EFEDE2' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#F5F4EA', border: '1px solid #E4E1D2', borderRadius: 10,
              padding: '8px 10px',
            }}>
              <Search size={15} color="#9AA088" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search family members\u2026"
                autoFocus
                style={{
                  border: 'none', outline: 'none', background: 'transparent',
                  fontSize: 14, fontFamily: FONT_B, color: '#2C2F26', flex: 1,
                }}
              />
            </div>
          </div>

          {/* List */}
          <div style={{ maxHeight: 264, overflowY: 'auto', padding: '6px 8px' }}>
            {filtered.length === 0 && (
              <p style={{ textAlign: 'center', fontSize: 14, color: '#9AA088', padding: 12, fontFamily: FONT_B }}>
                No matches
              </p>
            )}
            {Object.keys(grouped).sort().map(letter => (
              <div key={letter}>
                {filtered.length > 8 && (
                  <p style={{ fontSize: 11, color: '#9AA088', textTransform: 'uppercase', margin: '8px 0 2px 6px', fontWeight: 700, fontFamily: FONT_B }}>
                    {letter}
                  </p>
                )}
                {grouped[letter].map(person => {
                  const isActive = person.id === selectedChildId || (person.id === null && !selectedChildId);
                  return (
                    <button key={person.id || 'self'} onClick={() => { onSelect(person.id); setOpen(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        padding: '9px 10px', borderRadius: 10, border: 'none', cursor: 'pointer',
                        background: isActive ? '#F0F5E8' : 'transparent', textAlign: 'left',
                        fontFamily: FONT_B, fontSize: 15, color: '#2C2F26',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F7F6EE'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', background: avatarColor(person.name),
                        color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{person.initial}</div>
                      <span style={{ fontWeight: isActive ? 700 : 500 }}>{person.name}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer for large families */}
          {allPeople.length > 20 && filtered.length < allPeople.length && (
            <div style={{
              borderTop: '1px solid #EFEDE2', padding: '8px 12px',
              textAlign: 'center', fontSize: 12.5, color: '#9AA088', fontFamily: FONT_B,
            }}>
              {allPeople.length - filtered.length} more &middot; keep typing to narrow down
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Most Recent Card ───
function MostRecentCard({ stories, navigate }) {
  if (!stories || stories.length === 0) return null;
  const latest = stories[0];
  return (
    <div style={{
      background: '#fff', border: '1px solid #EDEBDD', borderRadius: 18, padding: '18px 22px',
      display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28,
      boxShadow: '0 6px 24px rgba(54,60,40,.08), 0 2px 6px rgba(54,60,40,.05)',
      cursor: 'pointer',
    }} onClick={() => navigate(`/stories/${latest.id}`)}>
      <div style={{ width: 88, height: 88, borderRadius: 12, background: '#EFEEE5', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
        {latest.thumbnailUrl ? (
          <img src={latest.thumbnailUrl.startsWith('/') ? `${API_ORIGIN}${latest.thumbnailUrl}` : latest.thumbnailUrl} alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 35%', imageOrientation: 'from-image' }} loading="lazy" decoding="async" />
        ) : (
          <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#EAF0DE,#DCE6C8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BookOpen size={24} color="#556B2F" />
          </div>
        )}
        {latest.hasVideo && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,.92)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Play size={14} color="#2C2F26" style={{ marginLeft: 2 }} />
            </div>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#C65D00', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 3, fontFamily: FONT_B }}>MOST RECENT</div>
        <div style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 17, color: '#2C2F26', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{latest.title || 'Untitled'}</div>
        <div style={{ fontSize: 13, color: '#828A76', fontFamily: FONT_B, marginTop: 2 }}>Added {latest.date ? new Date(latest.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>
      </div>
      <button style={{ background: '#556B2F', color: '#fff', fontWeight: 700, fontSize: 14, padding: '10px 18px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT_B, whiteSpace: 'nowrap' }}>
        Open story
      </button>
    </div>
  );
}

// ─── Capped Preview (max 4 thumbs inside chapter card) ───
function CappedPreview({ chapter, navigate }) {
  const previews = chapter.memories_preview?.slice(0, 4) || [];
  if (previews.length === 0) return null;
  const total = (chapter.memory_count || 0) + (chapter.milestone_count || 0);
  const remaining = total - 3;

  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
      {previews.slice(0, remaining > 0 ? 3 : 4).map((item, i) => {
        const thumbUrl = resolveThumbUrl({ url: item.thumbnail_url || item.cover_url });
        return (
          <div key={item.id || i} style={{
            width: 72, height: 56, borderRadius: 9, overflow: 'hidden',
            background: thumbUrl ? 'transparent' : ['#E8E4D6', '#D8DCC8', '#EDE6DA', '#DBD5C4'][i % 4],
            cursor: 'pointer',
          }} onClick={e => { e.stopPropagation(); navigate(`/my-life/chapters/${chapter.id}`); }}>
            {thumbUrl && (
              <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
            )}
          </div>
        );
      })}
      {remaining > 0 && (
        <div style={{
          width: 72, height: 56, borderRadius: 9, border: '1px dashed #CFCCBA',
          background: '#F1EFE4', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 600, color: '#8A9077', fontFamily: FONT_B, cursor: 'pointer',
        }} onClick={e => { e.stopPropagation(); navigate(`/my-life/chapters/${chapter.id}`); }}>
          +{remaining}
        </div>
      )}
    </div>
  );
}

// ─── Latest Post Card (fetches + renders StoryCard inside chapter) ───
function LatestPostCard({ lifeEventId, navigate }) {
  const [storyData, setStoryData] = useState(null);

  useEffect(() => {
    if (!lifeEventId) return;
    lifeEventAPI.getEvent(lifeEventId)
      .then(s => {
        let media = (s.media_files || []).filter(m => m.media_type === 'image' || !m.media_type);
        if (media.length === 0 && s.thumbnailUrl) {
          const thumbUrl = s.thumbnailUrl.startsWith('/') ? `${API_ORIGIN}${s.thumbnailUrl}` : s.thumbnailUrl;
          media = [{ id: 'thumb', url: thumbUrl, media_type: 'image' }];
        }
        media = media.map(m => ({
          ...m,
          url: m.file_url || (m.url && m.url.startsWith('/') ? `${API_ORIGIN}${m.url}` : m.url) || (m.id ? getMediaServeUrl(m.id) : ''),
        }));
        const authorName = s.user_name || s.author_name || `${s.user_first_name || ''}`.trim() || 'Someone';
        setStoryData({
          id: s.id,
          title: s.title || 'Untitled',
          story: s.story || '',
          category: s.event_data?.story_type || s.story_type || 'family_memory',
          date: s.date || s.created_at,
          location: s.location || '',
          author: {
            name: authorName,
            initial: authorName.charAt(0).toUpperCase(),
            color: '#3E4F22',
          },
          media,
          loveCount: 0,
          viewerSentLove: false,
          commentCount: 0,
        });
      })
      .catch(() => {});
  }, [lifeEventId]);

  if (!storyData) return null;

  return (
    <StoryCard story={storyData} onClick={() => navigate(`/stories/${storyData.id}`)} />
  );
}

// ─── Story Row (thumbnail + title + date; shared by chapter cards and children's cards) ───
function StoryRow({ href, navigate, thumbnail, title, meta }) {
  const thumbUrl = thumbnail
    ? (thumbnail.startsWith('http') ? thumbnail : `${API_ORIGIN}${thumbnail}`)
    : null;
  return (
    <a href={href}
      onClick={e => { e.preventDefault(); navigate(href); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: 12,
        border: '1.5px solid #E4E1D2', borderRadius: 12, textDecoration: 'none', color: 'inherit',
      }}
    >
      <div style={{
        width: 92, height: 68, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
        background: '#EFEDE3', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {thumbUrl ? (
          <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
        ) : (
          <BookOpen size={24} color="#3F4436" />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 17, color: '#2C2F26', fontFamily: FONT_B }}>{title}</div>
        <div style={{ fontSize: 14.5, color: '#4A4F40', fontFamily: FONT_B }}>{meta}</div>
      </div>
      <ChevronRight size={22} color="#3F4436" style={{ flexShrink: 0 }} />
    </a>
  );
}

// ─── Chapter Station (three states: empty / current / future) ───
function ChapterStation({ chapter, index, navigate, defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const storyCount = (chapter.memory_count || 0) + (chapter.milestone_count || 0);
  const isWisdom = chapter.band_key === 'wisdom' || (chapter.title || '').toLowerCase().includes('wisdom');
  const title = (chapter.title || '').replace(/^"|"$/g, ''); // strip quotes
  const yearRange = isWisdom ? 'Advice for the family' : `${chapter.start_year || '?'} – ${chapter.end_year || 'now'}`;
  const previews = (chapter.memories_preview || []).slice(0, 2);

  return (
    <div style={{
      background: '#fff', border: expanded ? '2px solid #3E4F22' : '1.5px solid #D6D1BD',
      borderRadius: 14, overflow: 'hidden',
    }}>
      {/* Header row — toggle button */}
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 18px', minHeight: 60, border: 'none', cursor: 'pointer',
          background: expanded ? '#F1F5E8' : '#fff', textAlign: 'left',
        }}
      >
        <div style={{
          width: 34, height: 34, borderRadius: '50%', background: '#3E4F22',
          color: '#fff', fontSize: 14.5, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>{chapter.number || index}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 16.5, color: '#2C2F26', fontFamily: FONT_B }}>{title}</div>
          <div style={{ fontSize: 14, color: '#4A4F40', fontFamily: FONT_B }}>
            {yearRange} · {storyCount} {storyCount === 1 ? 'story' : 'stories'}
          </div>
        </div>
        {expanded
          ? <ChevronRight size={22} color="#3E4F22" style={{ transform: 'rotate(-90deg)', flexShrink: 0 }} />
          : <ChevronRight size={22} color="#3E4F22" style={{ transform: 'rotate(90deg)', flexShrink: 0 }} />
        }
      </button>

      {/* Expanded body — story rows */}
      {expanded && (
        <div style={{ padding: '12px 18px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {previews.map(mem => {
            const href = mem.life_event_id ? `/stories/${mem.life_event_id}` : `/my-life/chapters/${chapter.id}`;
            return (
              <StoryRow key={mem.id} href={href} navigate={navigate}
                thumbnail={mem.thumbnail_url} title={mem.title} meta={mem.date_display || mem.year} />
            );
          })}
          {storyCount > 2 && (
            <button onClick={() => navigate(`/my-life/chapters/${chapter.id}`)} style={{
              background: 'none', border: 'none', color: '#3E4F22', fontSize: 15,
              fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
              textDecoration: 'underline', textUnderlineOffset: '3px',
              padding: '10px 2px', textAlign: 'left', whiteSpace: 'nowrap',
            }}>
              See all {storyCount} stories in this chapter
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════
export default function MyLifePrompt() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const externalPersonId = searchParams.get('person') || null;
  const userData = getUser() || {};

  // Person switcher state
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [myPhotoUrl, setMyPhotoUrl] = useState(null);
  const personId = externalPersonId || selectedChildId || null;

  // Data
  const { profile, chapters, legacyStats, stories: personStories, hasCompletedOnboarding, loading } = useChaptersData(personId);
  const [suggestions, setSuggestions] = useState([]);
  const [sinceData, setSinceData] = useState(null);
  const [pendingVoices, setPendingVoices] = useState(0);
  const [pendingJobId, setPendingJobId] = useState(null);

  // Ask state
  const [askState, setAskState] = useState('default'); // default | loading | answer | empty | error
  const [askResponse, setAskResponse] = useState(null);
  const [recentQuestions, setRecentQuestions] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mylife_recent_q') || '[]'); } catch { return []; }
  });
  const [activeQuestionIdx, setActiveQuestionIdx] = useState(-1);
  const [searchHistory, setSearchHistory] = useState([]);
  const [activeHistoryId, setActiveHistoryId] = useState(null);
  const [activeQuery, setActiveQuery] = useState('');
  const activeQueryRef = useRef('');

  // Children's stories data (13b)
  const [childStories, setChildStories] = useState({}); // { [kidId]: { total, items: [last 2 added] } }

  // Load family members for switcher
  useEffect(() => {
    if (externalPersonId) return;
    axiosInstance.get('/genealogy/people/my_family_grouped/').then(res => {
      const d = res.data || {};
      // Fetch story counts for minor children
      const kids = (d.children || []).filter(c => c.is_minor);
      if (kids.length > 0) {
        const byKid = {};
        Promise.all(kids.map(k =>
          axiosInstance.get(`/lifeevent/feed/?person_id=${k.id}&limit=2`).then(r => {
            byKid[k.id] = { total: r.data.total || 0, items: r.data.items || [] };
          }).catch(() => { byKid[k.id] = { total: 0, items: [] }; })
        )).then(() => setChildStories(byKid));
      }
      const members = [
        ...(d.spouse ? [d.spouse] : []),
        ...(d.children || []),
        ...(d.parents || []),
        ...(d.siblings || []),
        ...(d.other || []),
      ];
      setChildren(members);
    }).catch(() => {});
    axiosInstance.get('/account/api/accounts/profile/').then(res => {
      const url = res.data?.profile_photo_url;
      if (url) setMyPhotoUrl(url.startsWith('http') ? url : `${API_ORIGIN}${url}`);
    }).catch(() => {});
  }, [externalPersonId]);

  // Load suggestions + brief + search history
  useEffect(() => {
    getSuggestionChips().then(c => { if (c?.length) setSuggestions(c); }).catch(() => {});
    axiosInstance.get('/retrieval/since-last-visit/').then(r => setSinceData(r.data)).catch(() => {});
    getPendingConfirmations().then(r => {
      setPendingVoices(r?.total - r?.named || 0);
      if (r?.job_ids?.length > 0) setPendingJobId(r.job_ids[0]);
    }).catch(() => {});
    getSearchHistory(null, 20).then(r => setSearchHistory(r.entries || [])).catch(() => {});
  }, []);

  // Ask handler
  const handleAsk = useCallback(async (question) => {
    setAskState('loading');
    setAskResponse(null);
    setActiveQuery(question);
    activeQueryRef.current = question;

    // Save to recent
    setRecentQuestions(prev => {
      const updated = [question, ...prev.filter(q => q !== question)].slice(0, 10);
      localStorage.setItem('mylife_recent_q', JSON.stringify(updated));
      return updated;
    });
    setActiveQuestionIdx(0);

    try {
      // Phase 1: fast results without LLM composition (~200ms)
      const fast = await askQuestion(question, true);
      if (activeQueryRef.current !== question) return;

      const confidence = fast.meta?.confidence;
      const isMetadata = fast.meta?.intent === 'list_by_person' || fast.meta?.intent === 'timeline';

      if (fast.status === 'empty' || !fast.items?.length) {
        setAskState('empty');
        setAskResponse(fast);
      } else if (isMetadata || confidence === 'confident') {
        // High confidence or metadata query — show immediately, no compose needed
        setAskState('answer');
        setAskResponse(fast);
      } else {
        // Low/marginal confidence — wait for LLM compose to filter properly
        // Show loading while compose runs (don't flash irrelevant results)
        setAskState('loading');
      }

      // Phase 2: compose in background (unless metadata query which skips it)
      if (!isMetadata && fast.items?.length > 0) {
        askQuestion(question, false).then(composed => {
          if (activeQueryRef.current !== question) return;
          if (composed.status === 'empty' || !composed.items?.length) {
            setAskState('empty');
            setAskResponse(composed);
          } else {
            setAskState('answer');
            setAskResponse(composed);
          }
        }).catch(() => {
          // On compose failure, show Phase 1 results as fallback
          if (activeQueryRef.current !== question) return;
          if (fast.items?.length) {
            setAskState('answer');
            setAskResponse(fast);
          }
        });
      }
      // Refresh history
      getSearchHistory(null, 20).then(r => setSearchHistory(r.entries || [])).catch(() => {});
    } catch (err) {
      if (activeQueryRef.current !== question) return;
      setAskState('error');
      setAskResponse({ status: 'error' });
    }
  }, []);

  const handleBack = () => {
    setAskState('default');
    setAskResponse(null);
    setActiveQuestionIdx(-1);
    setActiveHistoryId(null);
    setActiveQuery('');
    activeQueryRef.current = '';
  };

  const handleHistorySelect = (entry) => {
    setActiveHistoryId(entry.id);
    handleAsk(entry.query_text);
  };

  const handleRecentSelect = (idx) => {
    const q = recentQuestions[idx];
    if (q) handleAsk(q);
  };

  if (loading) {
    return (
      <HubLayout activeView="life">
        <div style={{ maxWidth: 920, margin: '0 auto', padding: '32px', textAlign: 'center', color: '#828A76' }}>
          Loading your story...
        </div>
      </HubLayout>
    );
  }

  if (!personId && !hasCompletedOnboarding) {
    return <ChaptersOnboarding userName={profile?.first_name || userData.first_name || 'Friend'} birthYear={profile?.birth_year || null} />;
  }

  // Generate chapters for another person who has none yet
  const generateForPerson = async () => {
    if (!personId || !profile?.birth_year) return;
    try {
      await axiosInstance.post('/child-chapters/generate_child_chapters/', { person_id: personId });
    } catch {
      // Fallback to regular generate — backend creates for the person_id context
      await axiosInstance.post('/chapters/generate/', { birth_year: profile.birth_year, person_id: personId });
    }
    window.location.reload();
  };

  const totalChapters = chapters.length;
  const totalMemories = chapters.reduce((s, c) => s + (c.memory_count || 0), 0);
  const totalStories = personStories?.length || 0;

  const historySidebar = searchHistory.length > 0 ? (
    <SearchHistorySidebar
      entries={searchHistory}
      activeId={activeHistoryId}
      onSelect={handleHistorySelect}
    />
  ) : null;

  return (
    <HubLayout activeView="life" sidebarExtra={historySidebar}>
      <div className="ml-content" style={{ maxWidth: 1240, margin: '0 auto', padding: '28px 30px 80px' }}>

        {/* BAND 1: Greeting + "Whose life" switcher */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <Greeting profile={profile} totalChapters={totalChapters} totalStories={totalStories} />
          <PersonSwitcher
            currentName={profile?.first_name || 'Me'}
            children={children}
            selectedChildId={selectedChildId}
            onSelect={(childId) => { setSelectedChildId(childId); handleBack(); }}
          />
        </div>

          {/* BAND 2: Prompt Bar */}
          <PromptBar onSubmit={handleAsk} suggestions={suggestions} activeQuery={activeQuery} />

          {/* BAND 3: Brief */}
          <Reminders pendingVoices={pendingVoices} pendingJobId={pendingJobId} navigate={navigate} />

          {/* BAND 4: Content */}
          {askState !== 'default' ? (
            <AskResults
              response={askResponse}
              loading={askState === 'loading'}
              onBack={handleBack}
              query={activeQuery}
            />
          ) : personId && !hasCompletedOnboarding && profile?.birth_year ? (
            /* Viewing another person who has no chapters yet */
            <div style={{
              textAlign: 'center', padding: '40px 20px', background: '#fff',
              borderRadius: 18, border: '1.5px dashed #D8D3BE',
            }}>
              <Feather size={36} color="#C65D00" style={{ marginBottom: 12 }} />
              <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 22, color: '#2C2F26', margin: '0 0 8px' }}>
                {profile.first_name}'s story hasn't started yet
              </h3>
              <p style={{ fontSize: 16, color: '#6E7562', fontFamily: FONT_B, margin: '0 0 20px', lineHeight: 1.5 }}>
                Create chapters for {profile.first_name}'s life so you can add stories, milestones, and memories.
              </p>
              <button onClick={generateForPerson} style={{
                background: '#E65100', color: '#fff', fontWeight: 700, fontSize: 16,
                padding: '14px 28px', borderRadius: 999, border: 'none', cursor: 'pointer',
                fontFamily: FONT_B, minHeight: 44,
              }}>
                Create {profile.first_name}'s chapters
              </button>
            </div>
          ) : (
            <>
              {/* Section header */}
              {(() => {
                const withStories = chapters.filter(c => (c.memory_count || 0) + (c.milestone_count || 0) > 0);
                const withoutStories = chapters.filter(c => (c.memory_count || 0) + (c.milestone_count || 0) === 0);
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                      <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 23, color: '#2C2F26', margin: 0 }}>
                        {profile?.first_name || 'Your'}'s life in chapters
                      </h2>
                      <span style={{ fontSize: 14.5, color: '#4A4F40', fontFamily: FONT_B }}>
                        {withStories.length} of {chapters.length} chapter{chapters.length !== 1 ? 's' : ''} {withStories.length === 1 ? 'has' : 'have'} stories
                      </span>
                    </div>

                    {/* Chapters with stories */}
                    {withStories.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                        {withStories.map((ch) => {
                          const isCurrent = !ch.end_year || ch.end_year >= new Date().getFullYear();
                          return (
                            <ChapterStation key={ch.id} chapter={ch} index={ch.number} navigate={navigate} defaultExpanded={isCurrent} />
                          );
                        })}
                      </div>
                    )}

                    {/* Empty chapters — compact grid */}
                    {withoutStories.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <h3 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 18, color: '#2C2F26', margin: '0 0 4px' }}>
                          Chapters waiting for their first story
                        </h3>
                        <p style={{ fontSize: 14, color: '#4A4F40', fontFamily: FONT_B, margin: '0 0 12px' }}>
                          Tap one to add a memory to it.
                        </p>
                        <div style={{
                          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                          gap: 8,
                        }}>
                          {withoutStories.map((ch) => (
                            <button key={ch.id}
                              onClick={() => navigate(`/tell-a-story?chapter=${ch.id}`)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                border: '1.5px dashed #9A9F8A', borderRadius: 12,
                                background: 'transparent', padding: '10px 14px', minHeight: 52,
                                cursor: 'pointer', textAlign: 'left',
                              }}
                            >
                              <div style={{
                                width: 28, height: 28, borderRadius: '50%', background: '#EFEDE3',
                                border: '2px solid #9A9F8A', color: '#3F4436', fontSize: 13, fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              }}>{ch.number}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 15.5, color: '#2C2F26', fontFamily: FONT_B }}>{ch.title}</div>
                                <div style={{ fontSize: 13.5, color: '#4A4F40', fontFamily: FONT_B }}>
                                  {(ch.band_key === 'wisdom' || (ch.title || '').toLowerCase().includes('wisdom'))
                                    ? 'Advice for the family'
                                    : `${ch.start_year}–${ch.end_year || 'Present'}`}
                                </div>
                              </div>
                              <span style={{ fontSize: 14, fontWeight: 700, color: '#3E4F22', fontFamily: FONT_B, whiteSpace: 'nowrap' }}>+ Add</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── 13b: Your children's stories ── */}
              {!personId && (() => {
                const minorKids = children.filter(c => c.is_minor || c.age < 18);
                if (minorKids.length === 0) return null;
                return (
                  <div style={{ marginTop: 32 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                      <h2 style={{ fontFamily: FONT_H, fontWeight: 700, fontSize: 21, color: '#2C2F26', margin: 0 }}>
                        Your children's stories
                      </h2>
                      <span style={{ fontSize: 14, color: '#828A76', fontFamily: FONT_B }}>
                        Stories you've added for them
                      </span>
                    </div>
                    <p style={{ fontSize: 15, color: '#828A76', fontFamily: FONT_B, margin: '0 0 16px' }}>
                      Each child has their own life, organized by school year.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {minorKids.map(kid => {
                        const { total: count = 0, items: latest = [] } = childStories[kid.id] || {};
                        const kidName = kid.first_name || kid.name || '?';
                        const kidInitial = kidName[0].toUpperCase();
                        const agePart = kid.age != null ? `Age ${kid.age}` : '';
                        const meta = [agePart, `${count} ${count === 1 ? 'story' : 'stories'}`].filter(Boolean).join(' · ');
                        const outlineBtn = {
                          minHeight: 44, border: '1px solid #D4D0C0', borderRadius: 10,
                          background: '#fff', cursor: 'pointer', padding: '0 16px',
                          fontFamily: FONT_B, fontSize: 15, fontWeight: 700, color: '#3E4F22',
                          display: 'flex', alignItems: 'center', gap: 6,
                        };
                        return (
                          <div key={kid.id} style={{
                            background: '#fff', border: '1.5px solid #D6D1BD', borderRadius: 14, overflow: 'hidden',
                          }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', minHeight: 60,
                              background: latest.length > 0 ? '#F1F5E8' : '#fff',
                            }}>
                              <div style={{
                                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                                background: '#6B8E23', color: '#fff', fontWeight: 700, fontSize: 18,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>{kidInitial}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: 16.5, color: '#2C2F26', fontFamily: FONT_B }}>{kidName}</div>
                                <div style={{ fontSize: 14, color: '#4A4F40', fontFamily: FONT_B }}>{meta}</div>
                              </div>
                              {count > 0 ? (
                                <button onClick={() => setSelectedChildId(kid.id)} style={outlineBtn}>
                                  Open {kidName}'s life <ChevronRight size={16} />
                                </button>
                              ) : (
                                <button onClick={() => navigate(`/tell-a-story?person=${kid.id}`)} style={outlineBtn}>
                                  <Plus size={16} /> Add a story
                                </button>
                              )}
                            </div>
                            {latest.length > 0 && (
                              <div style={{ padding: '12px 18px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {latest.map(item => (
                                  <StoryRow key={item.id} href={`/stories/${item.id}`} navigate={navigate}
                                    thumbnail={item.cover_url} title={item.title}
                                    meta={item.date ? item.date.slice(0, 4) : ''} />
                                ))}
                                {count > latest.length && (
                                  <button onClick={() => setSelectedChildId(kid.id)} style={{
                                    background: 'none', border: 'none', color: '#3E4F22', fontSize: 15,
                                    fontWeight: 700, cursor: 'pointer', fontFamily: FONT_B,
                                    textDecoration: 'underline', textUnderlineOffset: '3px',
                                    padding: '10px 2px', textAlign: 'left', whiteSpace: 'nowrap',
                                  }}>
                                    See all {count} stories for {kidName}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
      </div>

      <style>{`
        @keyframes pulseRing {
          0% { box-shadow: 0 0 0 0 rgba(230,81,0,.25); }
          70% { box-shadow: 0 0 0 10px rgba(230,81,0,0); }
          100% { box-shadow: 0 0 0 0 rgba(230,81,0,0); }
        }
        .brief-pulse-tile { animation: pulseRing 2.6s ease-out infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) {
          * { transition: none !important; }
          .brief-pulse-tile { animation: none !important; }
        }
        @media (max-width: 640px) {
          .ml-content { padding: 16px 16px 60px !important; }
        }
      `}</style>
    </HubLayout>
  );
}
